import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, database, functions } from './firebase';
import { ref, get, set, update, onValue } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { useImpersonation } from './ImpersonationContext';

function TournamentDetails() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [matchesByRound, setMatchesByRound] = useState({});
  const [collapsedRounds, setCollapsedRounds] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tablets, setTablets] = useState({});
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [assignmentSuccess, setAssignmentSuccess] = useState(null); // {sessionId, tableName}
  const [matchSessions, setMatchSessions] = useState({}); // {matchId: sessionId}
  const [matchAssignments, setMatchAssignments] = useState({}); // {matchId: {tableId, tableNumber}}
  const [liveScores, setLiveScores] = useState({}); // {matchId: {p1Frame, p2Frame}}
  const [syncing, setSyncing] = useState(false); // For sync button loading state
  const [expandedMatches, setExpandedMatches] = useState({}); // {matchId: true/false} for compact view
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  
  // Impersonation context
  const { effectiveUserId, isImpersonating, actualUserId } = useImpersonation();
  
  // Responsive helper - visual only
  const isMobile = window.innerWidth < 768;

  // Define round order (bottom-up: earliest rounds first)
  const roundOrder = [
    'Last 64',
    'Last 32',
    'Last 16',
    'Round of 16',
    'Quarter final',
    'Semi final',
    'Third place playoff',
    'Final',
  ];

  // Helper function to organize matches by round
  const organizeMatchesByRound = (matches) => {
    const organized = {};
    (matches || []).forEach((match) => {
      const round = match.roundName || 'Unknown Round';
      if (!organized[round]) {
        organized[round] = [];
      }
      organized[round].push(match);
    });
    return organized;
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !effectiveUserId) {
      navigate('/organiser-login');
      return;
    }

    // Set up real-time listener for tournament data (including matches)
    const tournamentRef = ref(database, `organisers/${effectiveUserId}/tournaments/${tournamentId}`);
    
    const tournamentUnsubscribe = onValue(tournamentRef, (snapshot) => {
      if (snapshot.exists()) {
        const tournamentData = snapshot.val();
        console.log('🏆 Tournament updated:', {
          tournamentId: tournamentData.tournamentId,
          hasCuescoreId: !!tournamentData.tournamentId,
          matchCount: tournamentData.matches?.length || 0,
          allFields: Object.keys(tournamentData)
        });
        setTournament(tournamentData);
        setError(''); // Clear any previous error

        // Organize matches by round
        const organized = organizeMatchesByRound(tournamentData.matches);
        setMatchesByRound(organized);

        // Auto-collapse completed rounds (only on initial load or when rounds complete)
        setCollapsedRounds((prevCollapsed) => {
          const newCollapsed = { ...prevCollapsed };
          Object.keys(organized).forEach((round) => {
            const allFinished = organized[round].every((m) => m.matchstatus === 'finished');
            // Only auto-collapse if not already set and all finished (except Final)
            if (allFinished && round !== 'Final' && prevCollapsed[round] === undefined) {
              newCollapsed[round] = true;
            }
          });
          return newCollapsed;
        });

        setLoading(false);
      } else {
        // Only show error if we haven't loaded tournament data yet
        // This prevents brief "not found" during CueScore sync operations
        setTournament(currentTournament => {
          console.log('⚠️ Tournament snapshot empty - current state:', currentTournament ? 'exists' : 'null');
          if (!currentTournament) {
            // First load and no data - actually not found
            setError('Tournament not found');
            setLoading(false);
          } else {
            // We had data before, this might be a temporary sync issue
            console.log('⏳ Tournament data temporarily unavailable, keeping existing data...');
            // Don't clear tournament or set error - keep showing existing data
          }
          return currentTournament; // Keep existing data
        });
      }
    }, (err) => {
      console.error('Error loading tournament:', err);
      setError('Failed to load tournament');
      setLoading(false);
    });

    // Listen to tablet status in real-time
    const tabletsRef = ref(database, 'tablets');
    const tabletsUnsubscribe = onValue(tabletsRef, (snapshot) => {
      if (snapshot.exists()) {
        const tabletsData = snapshot.val();
        // Filter out offline tablets (no ping in last 60 seconds)
        const now = Date.now();
        const activeTablets = {};
        const assignments = {};
        
        Object.entries(tabletsData).forEach(([tableId, tablet]) => {
          // Only process tablets belonging to this organiser (using effectiveUserId for impersonation)
          if (tablet.organiserId !== effectiveUserId) {
            return;
          }
          
          // Always track assignments (even from offline tablets)
          if (tablet.currentMatch && tablet.currentMatch.matchId) {
            assignments[tablet.currentMatch.matchId] = {
              tableId: tableId,
              tableNumber: tablet.tableNumber,
              venueName: tablet.venueName,
            };
          }
          
          // Only include in active tablets if recently pinged
          if (tablet.lastPing && (now - tablet.lastPing < 60000)) {
            activeTablets[tableId] = tablet;
          }
        });
        
        setTablets(activeTablets);
        setMatchAssignments(assignments);
        console.log('📊 Tablet assignments updated:', assignments);
      } else {
        setTablets({});
        setMatchAssignments({});
      }
    });

    // Listen to match sessions
    const matchSessionsRef = ref(database, `organisers/${effectiveUserId}/matchSessions`);
    const sessionsUnsubscribe = onValue(matchSessionsRef, (snapshot) => {
      if (snapshot.exists()) {
        const sessionsData = snapshot.val();
        const sessions = {};
        Object.entries(sessionsData).forEach(([matchId, session]) => {
          sessions[matchId] = session.sessionId;
        });
        setMatchSessions(sessions);
      } else {
        setMatchSessions({});
      }
    });

    return () => {
      tournamentUnsubscribe();
      tabletsUnsubscribe();
      sessionsUnsubscribe();
    };
  }, [tournamentId, navigate, effectiveUserId]);

  // Subscribe to live scores for all matches with active sessions
  useEffect(() => {
    const unsubscribes = [];
    
    // For each match that has a session, subscribe to its live scores
    Object.entries(matchSessions).forEach(([matchId, sessionId]) => {
      const sessionRef = ref(database, `sessions/${sessionId}`);
      const unsubscribe = onValue(sessionRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setLiveScores(prev => ({
            ...prev,
            [matchId]: {
              p1Frame: data.p1Frame || 0,
              p2Frame: data.p2Frame || 0
            }
          }));
        }
      });
      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [matchSessions]);

  const generateSessionId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  const handleCreateShotClock = async (match) => {
    const user = auth.currentUser;
    if (!user || !effectiveUserId) return;

    // Check if this match already has a session
    const matchSessionRef = ref(database, `organisers/${effectiveUserId}/matchSessions/${match.matchId}`);
    const sessionSnapshot = await get(matchSessionRef);

    let sessionId;
    if (sessionSnapshot.exists()) {
      // Use existing session
      sessionId = sessionSnapshot.val().sessionId;
      console.log('Reopening existing session:', sessionId);
    } else {
      // Create new session
      sessionId = generateSessionId();
      
      // Store the session link
      await set(matchSessionRef, {
        sessionId: sessionId,
        matchId: match.matchId,
        tournamentId: tournament.tournamentId,
        createdAt: new Date().toISOString(),
      });
      
      console.log('Created new session:', sessionId);
    }

    // Pre-populate match data
    const matchData = {
      tournamentId: tournament.tournamentId,
      tournamentName: tournament.name,
      matchId: match.matchId,
      roundName: match.roundName,
      discipline: tournament.discipline,
      p1Name: match.playerA?.name || 'Player A',
      p1Image: match.playerA?.image || '',
      p2Name: match.playerB?.name || 'Player B',
      p2Image: match.playerB?.image || '',
      matchFormat: match.race || 5,
    };

    navigate(`/match/${sessionId}`, {state: matchData});
  };

  const toggleRound = (round) => {
    setCollapsedRounds((prev) => ({
      ...prev,
      [round]: !prev[round],
    }));
  };

  const handleSyncTournament = async () => {
    if (syncing || !tournament?.tournamentId) return;
    
    setSyncing(true);
    try {
      console.log('🔄 Syncing tournament from CueScore...');
      const syncTournament = httpsCallable(functions, 'syncSingleTournament');
      const result = await syncTournament({
        tournamentId: tournament.tournamentId,
        userId: effectiveUserId,
      });
      console.log('✅ Sync complete:', result.data);
      // UI will update automatically via onValue listener
    } catch (err) {
      console.error('❌ Sync failed:', err);
      alert('Failed to sync tournament. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSendNotification = async () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      alert('Please enter both a title and message');
      return;
    }
    
    setSendingNotification(true);
    try {
      const sendNotification = httpsCallable(functions, 'sendAdHocNotification');
      const result = await sendNotification({
        title: notificationTitle.trim(),
        message: notificationMessage.trim(),
        targetType: 'tournament',
        tournamentId: tournament.tournamentId.toString(),
        effectiveUserId: effectiveUserId,
      });
      
      console.log('📢 Notification sent:', result.data);
      
      if (result.data.sent > 0) {
        alert(`✅ Notification sent to ${result.data.sent} player(s)`);
      } else {
        alert('No premium players found in this tournament to notify');
      }
      
      setShowNotificationModal(false);
      setNotificationTitle('');
      setNotificationMessage('');
    } catch (err) {
      console.error('❌ Failed to send notification:', err);
      alert('Failed to send notification. Please try again.');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleAssignMatch = (match) => {
    console.log('📋 Match selected for assignment:', {
      matchId: match.matchId,
      hasCuescoreId: !!match.matchId,
      allFields: Object.keys(match)
    });
    setSelectedMatch(match);
    setShowAssignModal(true);
  };

  const assignMatchToTablet = async (tableId) => {
    const user = auth.currentUser;
    if (!user || !selectedMatch) return;

    try {
      // FIRST: Check if this match is already active in another session
      // Find the session with the HIGHEST score (most progress)
      let existingScores = null;
      let existingCueScoreData = null;
      let existingSessionId = null;
      let highestTotalScore = -1;
      let allOldSessionIds = []; // Track ALL sessions for this match
      
      const sessionsRef = ref(database, 'sessions');
      const sessionsSnap = await get(sessionsRef);
      
      if (sessionsSnap.exists()) {
        const allSessions = sessionsSnap.val();
        
        // Look for ALL sessions with this matchId and find the one with highest score
        for (const [sid, sessionData] of Object.entries(allSessions)) {
          // Only match if matchId exists and equals the CueScore match ID (use == for type coercion)
          if (sessionData.matchId && selectedMatch.matchId && sessionData.matchId == selectedMatch.matchId) {
            allOldSessionIds.push(sid); // Track for cleanup
            console.log(`🔍 Session ${sid} matchId: ${sessionData.matchId}, selectedMatch.matchId: ${selectedMatch.matchId}`);
            
            const p1 = sessionData.p1Frame || 0;
            const p2 = sessionData.p2Frame || 0;
            const totalScore = p1 + p2;
            
            console.log(`🔍 Found session ${sid} with scores: ${p1}-${p2} (total: ${totalScore})`);
            
            // Keep the session with the highest total score
            if (totalScore > highestTotalScore) {
              highestTotalScore = totalScore;
              existingSessionId = sid;
              existingScores = { p1Frame: p1, p2Frame: p2 };
              
              // Extract CueScore data if it exists
              if (sessionData.cuescoreMatch) {
                existingCueScoreData = sessionData.cuescoreMatch;
              }
              
              console.log(`✅ This is now the best session (highest score: ${totalScore})`);
            } else {
              console.log(`⏭️ Skipping - lower score than current best (${highestTotalScore})`);
            }
          }
        }
      }
      
      if (existingScores && highestTotalScore > 0) {
        console.log('📊 Using session with highest score:', existingScores);
      } else if (existingSessionId) {
        console.log('📊 Found existing session but scores are 0-0');
      }
      
      // Reuse existing sessionId if found, otherwise generate new one
      const sessionId = existingSessionId || generateSessionId();
      console.log(existingSessionId ? '♻️ Reusing existing sessionId:' : '🆔 Generated new sessionId:', sessionId);
      const sessionRef = ref(database, `sessions/${sessionId}`);
      
      const sessionData = {
        sessionId: sessionId,
        matchId: selectedMatch.matchId,
        matchno: selectedMatch.matchno || null,  // CueScore match number for bracket progression
        winnerNext: selectedMatch.winnerNext || null,  // Next match number for winner
        loserNext: selectedMatch.loserNext || null,  // Next match number for loser (double elimination)
        tournamentId: tournament.tournamentId,
        tournamentName: tournament.name,
        roundName: selectedMatch.roundName,
        discipline: tournament.discipline,
        p1Name: selectedMatch.playerA?.name || 'Player A',
        p1Image: selectedMatch.playerA?.image || '',
        p1Id: selectedMatch.playerA?.playerId || null,  // CueScore player ID for match ending
        p2Name: selectedMatch.playerB?.name || 'Player B',
        p2Image: selectedMatch.playerB?.image || '',
        p2Id: selectedMatch.playerB?.playerId || null,  // CueScore player ID for match ending
        matchFormat: selectedMatch.raceTo || 5,
        tableName: `Table ${tablets[tableId].tableNumber}`,
        tableId: tableId,  // Store tableId for lag winner functionality from organiser control panel
        scoreboardCode: tablets[tableId].scoreboardCode,  // Store scoreboardCode so tablets can set lag winner without auth
        createdAt: Date.now(),
        organiserId: effectiveUserId,
        // Audit: track if action was taken by superAdmin impersonating
        ...(isImpersonating && { 
          impersonatedBy: actualUserId,
          impersonatedAt: Date.now()
        }),
      };
      
      // Add existing scores if found
      if (existingScores) {
        sessionData.p1Frame = existingScores.p1Frame;
        sessionData.p2Frame = existingScores.p2Frame;
        console.log('✅ Including existing scores in new session:', existingScores);
      }
      
      // Use update when reusing session to preserve lagWinner, lagWinnerConfirmed, etc.
      if (existingSessionId) {
        await update(sessionRef, sessionData);
        console.log('♻️ Updated existing session, preserving lagWinner state');
      } else {
        await set(sessionRef, sessionData);
      }
      
      // Add CueScore data if it exists
      if (existingCueScoreData) {
        await set(ref(database, `sessions/${sessionId}/cuescoreMatch`), existingCueScoreData);
        console.log('✅ Copied CueScore data to new session');
      }
      
      // Clean up ALL old sessions for this match
      if (allOldSessionIds.length > 0) {
        console.log(`🗑️ Cleaning up ${allOldSessionIds.length} old session(s)...`);
        
        for (const oldSid of allOldSessionIds) {
          if (oldSid !== sessionId) {
            try {
              await set(ref(database, `sessions/${oldSid}`), null);
              console.log(`  ✅ Deleted session: ${oldSid}`);
            } catch (cleanupError) {
              console.warn(`  ⚠️ Could not delete session ${oldSid}:`, cleanupError);
              // Don't fail the whole operation if cleanup fails
            }
          }
        }
      }

      // Also store under organiser for tracking
      const matchSessionRef = ref(database, `organisers/${effectiveUserId}/matchSessions/${selectedMatch.matchId}`);
      await set(matchSessionRef, {
        sessionId: sessionId,
        matchId: selectedMatch.matchId,
        tournamentId: tournament.tournamentId,
        createdAt: new Date().toISOString(),
      });

      // Assign match to tablet
      const tabletMatchRef = ref(database, `tablets/${tableId}/currentMatch`);
      await set(tabletMatchRef, {
        matchId: selectedMatch.matchId,
        sessionId: sessionId,
        tournamentId: tournament.tournamentId,
        tournamentName: tournament.name,
        roundName: selectedMatch.roundName,
        discipline: tournament.discipline,
        playerA: selectedMatch.playerA,
        playerB: selectedMatch.playerB,
        raceTo: selectedMatch.raceTo,
        assignedAt: Date.now(),
      });

      // Update tablet status
      const tabletStatusRef = ref(database, `tablets/${tableId}/status`);
      await set(tabletStatusRef, 'in-use');

      // Start match in CueScore if it has a matchId (CueScore ID)
      console.log('🔍 Checking if match has CueScore ID:', {
        matchId: selectedMatch.matchId,
        tournamentId: tournament.tournamentId,
        allMatchFields: Object.keys(selectedMatch)
      });
      
      console.log('🆔 SessionId right before Cloud Function:', sessionId, typeof sessionId);
      
      if (selectedMatch.matchId && tournament.tournamentId) {
        try {
          // Check if we already found CueScore data (match already started)
          if (existingCueScoreData) {
            console.log('⏭️ Match already started in CueScore (session: ' + existingSessionId + '), skipping start call');
            // CueScore data already copied above
          } else {
            console.log('Starting match in CueScore...');
            
            const payload = {
              tournamentId: tournament.tournamentId, // CueScore tournament ID
              matchId: selectedMatch.matchId, // CueScore match ID
              userId: effectiveUserId, // Pass userId explicitly as fallback
            tableId: tableId, // TABLE ALLOCATION
            };
            
            console.log('📤 Sending to Cloud Function:', payload);
            
            const startCueScoreMatch = httpsCallable(functions, 'startCueScoreMatch');
            
            const result = await startCueScoreMatch(payload);
            
            console.log('✅ Match started in CueScore:', result);
            
            // Store CueScore match info in session for later updates
            await set(ref(database, `sessions/${sessionId}/cuescoreMatch`), {
              tournamentId: tournament.tournamentId,
              matchId: selectedMatch.matchId,
              startedAt: Date.now(),
            });
          }
        } catch (cuescoreError) {
          console.error('⚠️ Failed to start match in CueScore:', cuescoreError);
          console.error('Error details:', cuescoreError.message, cuescoreError.code);
          // Don't block the assignment if CueScore fails
          alert('Match assigned to tablet, but failed to start in CueScore. You may need to start it manually.');
        }
      } else {
        console.log('⏭️ Skipping CueScore start - missing matchId or tournamentId');
      }

      setShowAssignModal(false);
      setAssignmentSuccess({
        sessionId: sessionId,
        tableName: `Table ${tablets[tableId].tableNumber}`,
        tableId: tableId,
      });
      
      // Send SMS notifications to players (only if they have phone numbers)
      try {
        console.log('📱 Sending push notifications...');
        const sendMatchNotification = httpsCallable(functions, 'sendMatchNotification');
        const smsResult = await sendMatchNotification({
          playerAId: selectedMatch.playerA?.playerId,
          playerAName: selectedMatch.playerA?.name || 'Player A',
          playerBId: selectedMatch.playerB?.playerId,
          playerBName: selectedMatch.playerB?.name || 'Player B',
          tableNumber: tablets[tableId].tableNumber,
          tableName: tablets[tableId].venueName || '',
          tournamentName: tournament.name,
          // Deduplication fields - prevents duplicate notifications from CueScore polling
          tournamentId: tournament.tournamentId,
          matchId: selectedMatch.matchId,
          tableId: tablets[tableId].tableId || tableId
        });
        console.log('📱 Push notification result:', smsResult.data);
        
        // Show which players were notified
        if (smsResult.data?.results) {
          const { sent, skipped } = smsResult.data.results;
          if (sent.length > 0) {
            console.log(`✅ SMS sent to: ${sent.map(p => p.name).join(', ')}`);
          }
          if (skipped.length > 0) {
            console.log(`⏭️ SMS skipped (no number): ${skipped.map(p => p.name).join(', ')}`);
          }
        }
      } catch (smsError) {
        console.error('⚠️ Failed to send SMS notifications:', smsError);
        // Don't block - SMS is a nice-to-have
      }
      
      setSelectedMatch(null);
    } catch (err) {
      console.error('Error assigning match:', err);
      alert('Failed to assign match to tablet');
    }
  };

  const unassignMatch = async (matchId, tableId) => {
    if (!window.confirm('Remove match from tablet?')) {
      return;
    }

    try {
      // Clear the match from the tablet
      const tabletMatchRef = ref(database, `tablets/${tableId}/currentMatch`);
      await set(tabletMatchRef, null);

      // Update tablet status back to online
      const tabletStatusRef = ref(database, `tablets/${tableId}/status`);
      await set(tabletStatusRef, 'online');
    } catch (err) {
      console.error('Error unassigning match:', err);
      alert('Failed to unassign match');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      live: {text: '🔴 LIVE', color: '#16a34a'},
      upcoming: {text: '📅 Upcoming', color: '#2563eb'},
      completed: {text: '✓ Completed', color: '#6b7280'},
    };
    return badges[status] || badges.upcoming;
  };

  const getRoundIcon = (roundName) => {
    if (roundName.includes('Final')) return '🏆';
    if (roundName.includes('Semi')) return '⚔️';
    if (roundName.includes('Quarter')) return '🎯';
    return '🎱';
  };

  const getPlayerDisplay = (player, matchNumber) => {
    if (!player || !player.name) {
      return `Winner of Match ${matchNumber}`;
    }
    return player.name;
  };

  // Toggle match expanded/collapsed state
  const toggleMatchExpanded = (matchId) => {
    setExpandedMatches(prev => ({
      ...prev,
      [matchId]: !prev[matchId]
    }));
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#0d1321',
      color: '#f0f2f5',
      padding: '24px',
      fontFamily: "'DM Sans', sans-serif",
    },
    header: {
      maxWidth: '1200px',
      margin: '0 auto',
      marginBottom: '32px',
    },
    topBar: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '12px' : '0',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'stretch' : 'center',
      marginBottom: '24px',
    },
    backButton: {
      padding: '8px 16px',
      backgroundColor: '#1c2640',
      color: '#8b95a8',
      border: '1px solid #2a3555',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontFamily: "'DM Sans', sans-serif",
      transition: 'all 0.2s ease',
    },
    syncButton: {
      padding: '8px 16px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontFamily: "'DM Sans', sans-serif",
      fontWeight: '600',
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
      transition: 'all 0.2s ease',
    },
    title: {
      fontSize: isMobile ? '22px' : '28px',
      fontWeight: '700',
      margin: 0,
      fontFamily: "'Outfit', sans-serif",
      letterSpacing: '-0.01em',
    },
    subtitle: {
      fontSize: isMobile ? '15px' : '17px',
      color: '#8b95a8',
      marginTop: '8px',
    },
    meta: {
      display: 'flex',
      gap: '24px',
      marginTop: '16px',
      flexWrap: 'wrap',
    },
    metaItem: {
      fontSize: '13px',
      color: '#8b95a8',
    },
    roundsContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    roundSection: {
      marginBottom: '24px',
    },
    roundHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      backgroundColor: '#151c2e',
      borderRadius: '14px 14px 0 0',
      cursor: 'pointer',
      border: '1px solid #2a3555',
      borderBottom: 'none',
      transition: 'background-color 0.2s ease',
    },
    roundHeaderCollapsed: {
      borderRadius: '14px',
      border: '1px solid #2a3555',
    },
    roundTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '17px',
      fontWeight: '600',
      fontFamily: "'Outfit', sans-serif",
    },
    roundCount: {
      fontSize: '13px',
      color: '#5a6478',
    },
    matchesContainer: {
      backgroundColor: '#151c2e',
      borderRadius: '0 0 14px 14px',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      border: '1px solid #2a3555',
      borderTop: 'none',
    },
    matchCard: {
      backgroundColor: '#1c2640',
      borderRadius: '10px',
      padding: '16px',
      border: '1px solid #2a3555',
      transition: 'border-color 0.2s ease',
    },
    playerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
    },
    playerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    playerImage: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: '#2a3555',
      objectFit: 'cover',
    },
    playerName: {
      fontSize: '15px',
      fontWeight: '500',
    },
    playerNameWinner: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#34d399',
    },
    playerNameLoser: {
      fontSize: '15px',
      fontWeight: '400',
      color: '#5a6478',
    },
    score: {
      fontSize: '20px',
      fontWeight: 'bold',
      minWidth: '30px',
      textAlign: 'center',
      fontFamily: "'JetBrains Mono', monospace",
    },
    vs: {
      textAlign: 'center',
      color: '#5a6478',
      fontSize: '13px',
      padding: '4px 0',
    },
    notStartedBadge: {
      textAlign: 'center',
      padding: '8px',
      backgroundColor: '#1c2640',
      borderRadius: '6px',
      color: '#5a6478',
      fontSize: '13px',
      marginTop: '8px',
    },
    winnerBadge: {
      textAlign: 'center',
      padding: '8px',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      borderRadius: '6px',
      color: '#34d399',
      fontSize: '13px',
      marginTop: '8px',
    },
    createButton: {
      marginTop: '12px',
      padding: '10px 16px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      fontFamily: "'DM Sans', sans-serif",
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)',
      transition: 'all 0.2s ease',
    },
    viewButton: {
      marginTop: '12px',
      padding: '10px 16px',
      backgroundColor: '#1c2640',
      color: '#8b95a8',
      border: '1px solid #2a3555',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontFamily: "'DM Sans', sans-serif",
      width: '100%',
      transition: 'all 0.2s ease',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#0d1321',
    },
    spinner: {
      width: '48px',
      height: '48px',
      border: '3px solid #2a3555',
      borderTopColor: '#10b981',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    errorContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#0d1321',
      color: '#f0f2f5',
      fontFamily: "'DM Sans', sans-serif",
    },
    expandIcon: {
      fontSize: '18px',
      transition: 'transform 0.3s ease',
      color: '#5a6478',
    },
    // Modal styles
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: '#151c2e',
      borderRadius: '20px',
      padding: '24px',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto',
      border: '1px solid #2a3555',
      boxShadow: '0 12px 48px rgba(0, 0, 0, 0.5)',
    },
    modalTitle: {
      fontSize: '22px',
      fontWeight: '700',
      marginBottom: '20px',
      textAlign: 'center',
      fontFamily: "'Outfit', sans-serif",
    },
    matchInfo: {
      backgroundColor: '#1c2640',
      padding: '16px',
      borderRadius: '12px',
      marginBottom: '20px',
      border: '1px solid #2a3555',
    },
    modalSubtitle: {
      fontSize: '13px',
      color: '#8b95a8',
      textAlign: 'center',
    },
    modalPlayers: {
      fontSize: '17px',
      fontWeight: '600',
      textAlign: 'center',
      marginTop: '8px',
      fontFamily: "'Outfit', sans-serif",
    },
    tabletList: {
      marginBottom: '20px',
    },
    modalLabel: {
      fontSize: '12px',
      color: '#5a6478',
      marginBottom: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontWeight: '500',
    },
    noTablets: {
      textAlign: 'center',
      color: '#5a6478',
      padding: '20px',
      backgroundColor: '#1c2640',
      borderRadius: '10px',
      border: '1px solid #2a3555',
    },
    tabletItem: {
      width: '100%',
      padding: '16px',
      backgroundColor: '#1c2640',
      border: '1px solid #2a3555',
      borderRadius: '12px',
      marginBottom: '8px',
      cursor: 'pointer',
      textAlign: 'left',
      color: '#f0f2f5',
      fontFamily: "'DM Sans', sans-serif",
      transition: 'all 0.2s ease',
    },
    tabletItemHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
    },
    tabletNumber: {
      fontSize: '15px',
      fontWeight: '600',
      fontFamily: "'Outfit', sans-serif",
    },
    statusReady: {
      fontSize: '12px',
      color: '#10b981',
      fontWeight: '500',
    },
    statusBusy: {
      fontSize: '12px',
      color: '#ef4444',
      fontWeight: '500',
    },
    tabletVenue: {
      fontSize: '13px',
      color: '#8b95a8',
    },
    tabletCurrentMatch: {
      fontSize: '12px',
      color: '#f59e0b',
      marginTop: '4px',
    },
    modalCloseButton: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#1c2640',
      color: '#8b95a8',
      border: '1px solid #2a3555',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '15px',
      fontFamily: "'DM Sans', sans-serif",
      transition: 'all 0.2s ease',
    },
    // Success notification
    successNotification: {
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      backgroundColor: '#0d2818',
      borderRadius: '14px',
      padding: '16px 20px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
      border: '1px solid rgba(16, 185, 129, 0.2)',
      zIndex: 1000,
      maxWidth: '400px',
    },
    successContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    successIcon: {
      fontSize: '24px',
    },
    successText: {
      flex: 1,
    },
    successButton: {
      padding: '8px 12px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '600',
      fontFamily: "'DM Sans', sans-serif",
    },
    successCloseButton: {
      padding: '4px 8px',
      backgroundColor: 'transparent',
      color: '#5a6478',
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
    },
    assignedButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      backgroundColor: 'rgba(16, 185, 129, 0.12)',
      color: '#34d399',
      border: '1px solid rgba(16, 185, 129, 0.2)',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: '500',
    },
    unassignButton: {
      padding: '2px 6px',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
    },
    // Compact match styles
    compactMatchCard: {
      backgroundColor: '#1c2640',
      borderRadius: '10px',
      border: '1px solid #2a3555',
      overflow: 'hidden',
      transition: 'border-color 0.2s ease',
    },
    compactMatchRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      cursor: 'pointer',
      gap: '12px',
      minHeight: '52px',
    },
    compactPlayerSection: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
    },
    compactPlayerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flex: 1,
      justifyContent: 'flex-start',
      minWidth: 0,
    },
    compactPlayerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flex: 1,
      justifyContent: 'flex-end',
      minWidth: 0,
    },
    compactPlayerImage: {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      backgroundColor: '#2a3555',
      objectFit: 'cover',
      flexShrink: 0,
    },
    compactPlayerName: {
      fontSize: '14px',
      fontWeight: '500',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    compactPlayerNameWinner: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#34d399',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    compactPlayerNameLoser: {
      fontSize: '14px',
      fontWeight: '400',
      color: '#5a6478',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    compactScoreSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: 0,
      minWidth: '70px',
      justifyContent: 'center',
    },
    compactScore: {
      fontSize: '16px',
      fontWeight: 'bold',
      fontFamily: "'JetBrains Mono', monospace",
      minWidth: '20px',
      textAlign: 'center',
    },
    compactVs: {
      fontSize: '12px',
      color: '#5a6478',
    },
    compactTableBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      backgroundColor: 'rgba(16, 185, 129, 0.15)',
      color: '#34d399',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      flexShrink: 0,
    },
    compactStatusBadge: {
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      flexShrink: 0,
    },
    compactStatusNotStarted: {
      backgroundColor: 'rgba(90, 100, 120, 0.2)',
      color: '#8b95a8',
    },
    compactStatusPlaying: {
      backgroundColor: 'rgba(16, 185, 129, 0.15)',
      color: '#10b981',
    },
    compactStatusFinished: {
      backgroundColor: 'rgba(52, 211, 153, 0.1)',
      color: '#34d399',
    },
    compactAssignDropdown: {
      padding: '6px 12px',
      backgroundColor: '#059669',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '600',
      fontFamily: "'DM Sans', sans-serif",
      flexShrink: 0,
    },
    compactExpandIcon: {
      fontSize: '14px',
      color: '#5a6478',
      transition: 'transform 0.2s ease',
      flexShrink: 0,
    },
    compactExpandedSection: {
      padding: '12px 16px',
      borderTop: '1px solid #2a3555',
      backgroundColor: '#151c2e',
    },
    compactButtonRow: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
    },
    compactButton: {
      padding: '8px 14px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
      fontFamily: "'DM Sans', sans-serif",
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    compactWinnerBanner: {
      padding: '8px 12px',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      borderRadius: '6px',
      color: '#34d399',
      fontSize: '13px',
      marginBottom: '8px',
      textAlign: 'center',
    },
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={{fontSize: '18px', marginBottom: '16px'}}>{error}</p>
        <button onClick={() => navigate('/organiser-dashboard')} style={styles.backButton}>
          ← Back to Dashboard
        </button>
      </div>
    );
  }

  // Sort rounds by our defined order
  const sortedRounds = Object.keys(matchesByRound).sort((a, b) => {
    const indexA = roundOrder.indexOf(a);
    const indexB = roundOrder.indexOf(b);
    // If not found in order, put at the beginning
    const orderA = indexA === -1 ? -1 : indexA;
    const orderB = indexB === -1 ? -1 : indexB;
    return orderA - orderB;
  });

  return (
    <div style={styles.container}>
      {/* CSS for live score pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.topBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src="/icon.png" alt="Cue Clock Pro" style={{ width: '48px', height: '48px', borderRadius: '12px' }} />
            <span style={{ fontSize: '24px', fontWeight: '700', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}>Cue Clock Pro</span>
          </div>
          <button onClick={() => navigate('/organiser-dashboard')} style={styles.backButton}>
            ← Back to Tournaments
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h1 style={styles.title}>{tournament?.name}</h1>
          <button 
            onClick={handleSyncTournament} 
            disabled={syncing}
            style={{
              ...styles.syncButton,
              opacity: syncing ? 0.7 : 1,
              cursor: syncing ? 'not-allowed' : 'pointer',
            }}
            title="Sync with CueScore"
          >
            <span style={{ 
              display: 'inline-block',
              animation: syncing ? 'spin 1s linear infinite' : 'none',
            }}>
              ↻
            </span>
            {syncing ? ' Syncing...' : ' Sync'}
          </button>
          <button 
            onClick={() => setShowNotificationModal(true)} 
            style={{
              padding: '8px 16px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.2)',
            }}
            title="Send notification to tournament players"
          >
            📢 Notify
          </button>
        </div>
        <p style={styles.subtitle}>{tournament?.discipline}</p>

        <div style={styles.meta}>
          <span style={styles.metaItem}>
            📅 {tournament?.startDate && formatDate(tournament.startDate)}
            {tournament?.startDate !== tournament?.endDate &&
              tournament?.endDate &&
              ` - ${formatDate(tournament.endDate)}`}
          </span>
          <span style={styles.metaItem}>👥 {tournament?.playerCount || 0} Players</span>
          <span style={styles.metaItem}>🎱 {tournament?.matches?.length || 0} Matches</span>
        </div>

        {/* Available Tables Box */}
        {(() => {
          // Get the tournament venue name
          const tournamentVenue = tournament?.venues?.[0]?.name;
          
          if (!tournamentVenue) {
            return null; // No venue assigned to tournament
          }

          // Filter tablets for this venue
          const venueTablets = Object.entries(tablets).filter(
            ([_, tablet]) => tablet.venueName === tournamentVenue
          );

          if (venueTablets.length === 0) {
            return (
              <div style={{
                backgroundColor: '#374151',
                borderRadius: '8px',
                padding: '12px 16px',
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                  📍 {tournamentVenue} — No tablets online
                </span>
              </div>
            );
          }

          // Separate available and in-use tables
          const availableTables = venueTablets
            .filter(([_, tablet]) => tablet.status !== 'in-use')
            .map(([_, tablet]) => tablet.tableNumber)
            .sort((a, b) => a - b);

          const inUseTables = venueTablets
            .filter(([_, tablet]) => tablet.status === 'in-use')
            .map(([_, tablet]) => tablet.tableNumber)
            .sort((a, b) => a - b);

          return (
            <div style={{
              backgroundColor: '#1f2937',
              borderRadius: '8px',
              padding: '12px 16px',
              marginTop: '16px',
              border: availableTables.length > 0 ? '1px solid #16a34a' : '1px solid #374151'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                  📍 {tournamentVenue}
                </span>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {availableTables.length > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: '#16a34a', fontWeight: '500' }}>
                        ✓ Available:
                      </span>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {availableTables.map(tableNum => (
                          <span
                            key={tableNum}
                            style={{
                              backgroundColor: '#16a34a',
                              color: 'white',
                              padding: '2px 10px',
                              borderRadius: '12px',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}
                          >
                            {tableNum}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <span style={{ fontSize: '14px', color: '#f59e0b' }}>
                      ⏳ All tables in use
                    </span>
                  )}

                  {inUseTables.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>
                        In use:
                      </span>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {inUseTables.map(tableNum => (
                          <span
                            key={tableNum}
                            style={{
                              backgroundColor: '#dc2626',
                              color: 'white',
                              padding: '2px 10px',
                              borderRadius: '12px',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}
                          >
                            {tableNum}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Rounds */}
      <div style={styles.roundsContainer}>
        {sortedRounds.map((round) => {
          const matches = matchesByRound[round];
          const isCollapsed = collapsedRounds[round];
          const completedCount = matches.filter((m) => m.matchstatus === 'finished').length;
          const inProgressCount = matches.filter((m) => m.matchstatus === 'playing').length;

          return (
            <div key={round} style={styles.roundSection}>
              <div
                style={{
                  ...styles.roundHeader,
                  ...(isCollapsed ? styles.roundHeaderCollapsed : {}),
                }}
                onClick={() => toggleRound(round)}
              >
                <div style={styles.roundTitle}>
                  <span>{getRoundIcon(round)}</span>
                  <span>{round}</span>
                  <span style={styles.roundCount}>
                    ({completedCount}/{matches.length} complete
                    {inProgressCount > 0 && `, ${inProgressCount} in progress`})
                  </span>
                </div>
                <span
                  style={{
                    ...styles.expandIcon,
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  }}
                >
                  ▼
                </span>
              </div>

              {!isCollapsed && (
                <div style={styles.matchesContainer}>
                  {matches.map((match, index) => {
                    const isFinished = match.matchstatus === 'finished';
                    const isPlaying = match.matchstatus === 'playing';
                    const playerAWon = match.winner === 'A' || match.scoreA > match.scoreB;
                    const playerBWon = match.winner === 'B' || match.scoreB > match.scoreA;
                    const isExpanded = expandedMatches[match.matchId];
                    const hasTableAssignment = matchAssignments[match.matchId];
                    const hasSession = matchSessions[match.matchId];

                    // Get scores (live or final)
                    const scoreA = isFinished ? (match.scoreA || 0) : (liveScores[match.matchId]?.p1Frame || 0);
                    const scoreB = isFinished ? (match.scoreB || 0) : (liveScores[match.matchId]?.p2Frame || 0);
                    const showScore = isFinished || isPlaying || liveScores[match.matchId];

                    return (
                      <div key={match.matchId || index} style={styles.compactMatchCard}>
                        {/* Compact Row - Always Visible */}
                        <div 
                          style={styles.compactMatchRow}
                          onClick={() => toggleMatchExpanded(match.matchId)}
                        >
                          {/* Player A */}
                          <div style={styles.compactPlayerLeft}>
                            {match.playerA?.image ? (
                              <img
                                src={match.playerA.image}
                                alt={match.playerA?.name}
                                style={styles.compactPlayerImage}
                              />
                            ) : (
                              <div style={styles.compactPlayerImage}></div>
                            )}
                            <span style={
                              isFinished
                                ? playerAWon
                                  ? styles.compactPlayerNameWinner
                                  : styles.compactPlayerNameLoser
                                : styles.compactPlayerName
                            }>
                              {getPlayerDisplay(match.playerA, match.matchNumber)}
                            </span>
                          </div>

                          {/* Score Section */}
                          <div style={styles.compactScoreSection}>
                            {showScore ? (
                              <>
                                <span style={{
                                  ...styles.compactScore,
                                  ...(isPlaying && !isFinished ? { color: '#10b981' } : {})
                                }}>{scoreA}</span>
                                <span style={styles.compactVs}>-</span>
                                <span style={{
                                  ...styles.compactScore,
                                  ...(isPlaying && !isFinished ? { color: '#10b981' } : {})
                                }}>{scoreB}</span>
                              </>
                            ) : (
                              <span style={styles.compactVs}>vs</span>
                            )}
                          </div>

                          {/* Player B */}
                          <div style={styles.compactPlayerRight}>
                            <span style={
                              isFinished
                                ? playerBWon
                                  ? styles.compactPlayerNameWinner
                                  : styles.compactPlayerNameLoser
                                : styles.compactPlayerName
                            }>
                              {getPlayerDisplay(match.playerB, match.matchNumber)}
                            </span>
                            {match.playerB?.image ? (
                              <img
                                src={match.playerB.image}
                                alt={match.playerB?.name}
                                style={styles.compactPlayerImage}
                              />
                            ) : (
                              <div style={styles.compactPlayerImage}></div>
                            )}
                          </div>

                          {/* Table Badge or Assign Button */}
                          {!isFinished && (
                            hasTableAssignment ? (
                              <div 
                                style={styles.compactTableBadge}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span>📱 Table {hasTableAssignment.tableNumber}</span>
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    unassignMatch(match.matchId, hasTableAssignment.tableId);
                                  }}
                                  style={{ cursor: 'pointer', marginLeft: '4px' }}
                                  title="Unassign"
                                >
                                  ✕
                                </span>
                              </div>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssignMatch(match);
                                }}
                                style={styles.compactAssignDropdown}
                              >
                                📱 Assign
                              </button>
                            )
                          )}

                          {/* Status Badge */}
                          {isFinished && (
                            <span style={{...styles.compactStatusBadge, ...styles.compactStatusFinished}}>
                              ✓ Done
                            </span>
                          )}
                          {isPlaying && (
                            <span style={{...styles.compactStatusBadge, ...styles.compactStatusPlaying}}>
                              🔴 Live
                            </span>
                          )}
                          {!isFinished && !isPlaying && (
                            <span style={{...styles.compactStatusBadge, ...styles.compactStatusNotStarted}}>
                              ⏱️
                            </span>
                          )}

                          {/* Expand Icon */}
                          <span style={{
                            ...styles.compactExpandIcon,
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}>
                            ▼
                          </span>
                        </div>

                        {/* Expanded Section */}
                        {isExpanded && (
                          <div style={styles.compactExpandedSection}>
                            {/* Winner Banner for finished matches */}
                            {isFinished && (
                              <div style={styles.compactWinnerBanner}>
                                ✓ Winner: {playerAWon ? match.playerA?.name : match.playerB?.name}
                              </div>
                            )}

                            {/* Action Buttons */}
                            {!isFinished && (
                              <div style={styles.compactButtonRow}>
                                <button 
                                  onClick={() => handleCreateShotClock(match)} 
                                  style={styles.compactButton}
                                >
                                  🎯 {hasSession ? 'Start Shot Clock' : 'Create Shot Clock'}
                                </button>
                                
                                {hasSession && (
                                  <button 
                                    onClick={() => window.open(`/match/${hasSession}`, '_blank')} 
                                    style={{...styles.compactButton, backgroundColor: '#2563eb'}}
                                  >
                                    🎮 Control Panel
                                  </button>
                                )}
                              </div>
                            )}

                            {/* View Details for finished matches */}
                            {isFinished && (
                              <div style={styles.compactButtonRow}>
                                <button style={{...styles.compactButton, backgroundColor: '#1c2640', border: '1px solid #2a3555', color: '#8b95a8'}}>
                                  📊 View Match Details
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Assignment Modal */}
      {showAssignModal && selectedMatch && (
        <div style={styles.modalOverlay} onClick={() => setShowAssignModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>📱 Assign Match to Table</h2>
            
            <div style={styles.matchInfo}>
              <div style={styles.modalSubtitle}>{selectedMatch.roundName}</div>
              <div style={styles.modalPlayers}>
                {selectedMatch.playerA?.name} vs {selectedMatch.playerB?.name}
              </div>
            </div>

            <div style={styles.tabletList}>
              <div style={styles.modalLabel}>Available Tables ({Object.keys(tablets).length} online)</div>
              
              {Object.keys(tablets).length === 0 && (
                <div style={styles.noTablets}>
                  No tablets online. Please login tablets first.
                </div>
              )}

              {Object.entries(tablets).map(([tableId, tablet]) => {
                const isInUse = tablet.status === 'in-use';
                const currentMatchOnTable = tablet.currentMatch;
                
                return (
                  <button
                    key={tableId}
                    onClick={() => assignMatchToTablet(tableId)}
                    style={{
                      ...styles.tabletItem,
                      opacity: isInUse ? 0.5 : 1,
                      cursor: isInUse ? 'not-allowed' : 'pointer',
                    }}
                    disabled={isInUse}
                  >
                    <div style={styles.tabletItemHeader}>
                      <span style={styles.tabletNumber}>Table {tablet.tableNumber}</span>
                      <span style={isInUse ? styles.statusBusy : styles.statusReady}>
                        {isInUse ? '🔴 In Use' : '🟢 Ready'}
                      </span>
                    </div>
                    <div style={styles.tabletVenue}>📍 {tablet.venueName}</div>
                    {isInUse && currentMatchOnTable && (
                      <div style={styles.tabletCurrentMatch}>
                        Currently: {currentMatchOnTable.playerA?.name} vs {currentMatchOnTable.playerB?.name}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <button onClick={() => setShowAssignModal(false)} style={styles.modalCloseButton}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {assignmentSuccess && (
        <div style={styles.successNotification}>
          <div style={styles.successContent}>
            <div style={styles.successIcon}>✅</div>
            <div style={styles.successText}>
              <strong>Match Assigned!</strong>
              <div style={{fontSize: '14px', color: '#9ca3af', marginTop: '4px'}}>
                Sent to {assignmentSuccess.tableName}
              </div>
            </div>
            <div style={{display: 'flex', gap: '8px', marginLeft: 'auto'}}>
              <button
                onClick={() => window.open(`/match/${assignmentSuccess.sessionId}`, '_blank')}
                style={styles.successButton}
              >
                🎮 Open Control Panel
              </button>
              <button
                onClick={() => setAssignmentSuccess(null)}
                style={styles.successCloseButton}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showNotificationModal && (
        <div style={styles.modalOverlay} onClick={() => setShowNotificationModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>📢 Send Notification</h2>
            
            <div style={styles.matchInfo}>
              <div style={styles.modalSubtitle}>To all players in:</div>
              <div style={styles.modalPlayers}>{tournament?.name}</div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#8b95a8', marginBottom: '8px' }}>
                Title
              </label>
              <input
                type="text"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
                placeholder="e.g., Tournament Update"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#1c2640',
                  color: 'white',
                  border: '1px solid #2a3555',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#8b95a8', marginBottom: '8px' }}>
                Message
              </label>
              <textarea
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
                placeholder="Enter your message to players..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#1c2640',
                  color: 'white',
                  border: '1px solid #2a3555',
                  borderRadius: '8px',
                  fontSize: '16px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSendNotification}
                disabled={sendingNotification || !notificationTitle.trim() || !notificationMessage.trim()}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: sendingNotification ? '#4b5563' : '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: sendingNotification ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {sendingNotification ? '📤 Sending...' : '📢 Send Notification'}
              </button>
              <button
                onClick={() => setShowNotificationModal(false)}
                style={{
                  padding: '14px 24px',
                  backgroundColor: '#1c2640',
                  color: '#8b95a8',
                  border: '1px solid #2a3555',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Cancel
              </button>
            </div>

            <p style={{ marginTop: '16px', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
              Only premium players with the app installed will receive this notification.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentDetails;